import {
  CONTEXT_REQUIRED_HEADINGS,
  CONTEXT_SECTION_TITLES,
  CONTEXT_TEMPLATES,
  type ContextSection,
} from '@lixionary-jira/domain';

export interface ExternalPromptInput {
  project: { key: string; name: string; description: string };
  repository: { url: string; defaultBranch: string } | null;
  section: ContextSection;
  currentBody: string | null;
}

/**
 * A self-contained prompt an admin pastes into their own AI agent (Claude Code, Cursor, …) to draft one context
 * section. It asks for the same headings the completeness check uses, so the pasted answer is not flagged.
 */
export function buildExternalPrompt({
  project,
  repository,
  section,
  currentBody,
}: ExternalPromptInput): string {
  const title = CONTEXT_SECTION_TITLES[section];
  const headings = CONTEXT_REQUIRED_HEADINGS[section];
  const repo = repository
    ? `Repository: ${repository.url} (branch ${repository.defaultBranch}). If you have this repository checked out, inspect it to verify what you write.`
    : 'Repository: no repository is configured for this project. Do not guess at the code.';
  return `Draft the "${title}" section of the project context for ${project.name} (${project.key}) in Lixionary Jira. Coding agents and reviewers read this context before they work on tickets.

## Project
${project.name} (${project.key})
${project.description.trim() || '(No project description.)'}
${repo}

## Required headings
Use exactly these level-2 headings, in this order, and write content under every one. A heading with nothing beneath it is reported as incomplete.
${headings.map((h) => `- ${h}`).join('\n')}

## Template
\`\`\`markdown
${CONTEXT_TEMPLATES[section].trimEnd()}
\`\`\`

## Current ${title} version
${currentBody?.trim() ? `\`\`\`markdown\n${currentBody.trimEnd()}\n\`\`\`` : 'The section is empty.'}

## Rules
- Never include secret values: no passwords, tokens, API keys or private keys. Say where a secret lives (for example, "the DATABASE_URL environment variable") instead. Lixionary Jira refuses to save context that looks like it contains a secret.
- Keep what the current version already says unless it is wrong; fill in what is missing.
- Mark anything you could not verify with "(unverified)".
- Reply with the Markdown for this section only, with no preamble, so it can be pasted straight into the editor.`;
}
